import { activeRows, blankDraftRow, draftTotals, nextLocalId, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";
import { isFlatFeeCode } from "src/helpers/goaRules";
import type { Exam } from "src/types";

// Service-line templates for manually created invoices (chapter 18, stories 13/14) — shared by the
// creation wizard (insert / save in step 3), the invoice page (save an invoice's services) and
// Settings → Rechnungsvorlagen. A template can span several treatment days (`day`, 0 = first).

export type TemplateLine = {
    /** Treatment day within the template, 0 = first. Missing = 0. */
    day?: number;
    code: string;
    description: string;
    quantity: number;
    price: number;
    factor: number;
    justification: string;
    materialLines: { kind: string; label: string; cost: number }[];
};

export type InvoiceTemplate = {
    id: string;
    name: string;
    shared: boolean;
    lines: TemplateLine[];
    usageCount: number;
    lastUsedAt: string | null;
    updatedAt: string;
    /** Made by the viewer. */
    mine: boolean;
    /** The viewer may edit/delete it (creator, or an admin for shared ones). */
    canEdit: boolean;
};

const dayOf = (l: TemplateLine) => l.day ?? 0;

/** How many treatment days the template covers. */
export const templateDayCount = (template: Pick<InvoiceTemplate, "lines">) =>
    template.lines.reduce((n, l) => Math.max(n, dayOf(l) + 1), 0);

/** The lines of one template day (default: all days, in order). */
export const templateDayLines = (template: Pick<InvoiceTemplate, "lines">, day?: number) =>
    day === undefined ? template.lines : template.lines.filter((l) => dayOf(l) === day);

/** Template lines as new editor rows (fresh local ids every time — it can be inserted twice). */
export const templateRows = (template: Pick<InvoiceTemplate, "lines">, day?: number): GoaLineDraft[] =>
    templateDayLines(template, day).map((l) => ({
        ...blankDraftRow(),
        code: l.code,
        description: l.description,
        quantity: l.quantity,
        price: l.price,
        factor: l.factor,
        justification: l.justification ?? "",
        materialLines: (l.materialLines ?? []).map((m) => ({ _localId: nextLocalId(), kind: m.kind, label: m.label, cost: m.cost })),
    }));

/** The rows worth keeping in a template: not deleted, with a code — as lines of template day `day`. */
export const templateLinesFrom = (rows: GoaLineDraft[], day = 0): TemplateLine[] =>
    activeRows(rows)
        .filter((r) => r.code.trim())
        .map((r) => ({
            day,
            code: r.code.trim(),
            description: r.description,
            quantity: r.quantity,
            price: r.price,
            factor: r.factor,
            justification: r.justification,
            materialLines: r.materialLines.map((m) => ({ kind: m.kind, label: m.label, cost: m.cost })),
        }));

/** Several days' rows (in day order) as one multi-day template; days without lines are skipped. */
export const templateLinesFromDays = (days: GoaLineDraft[][]): TemplateLine[] => {
    const nonEmpty = days.map((rows) => templateLinesFrom(rows)).filter((lines) => lines.length > 0);
    return nonEmpty.flatMap((lines, day) => lines.map((l) => ({ ...l, day })));
};

/** An invoice's saved services as template lines — one template day per treatment date. PAD flat fees
 *  (Auslagen like SKL) can't be part of a manual invoice, so they're left out (`skipped`). */
export function templateLinesFromExams(exams: Exam[]): { lines: TemplateLine[]; skipped: number } {
    const byDate = new Map<string, Exam[]>();
    let skipped = 0;
    for (const e of exams) {
        if (!e.code?.trim() || isFlatFeeCode(e.code)) {
            skipped++;
            continue;
        }
        const date = e.date ? String(e.date).slice(0, 10) : "";
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date)!.push(e);
    }
    const dates = [...byDate.keys()].sort();
    const lines = dates.flatMap((date, day) =>
        byDate.get(date)!.map((e) => ({
            day,
            code: e.code.trim(),
            description: e.description ?? "",
            quantity: e.quantity ?? 1,
            price: e.price ?? 0,
            factor: e.factor ?? 1,
            justification: e.justification ?? "",
            materialLines: (e.materialLines ?? []).map((m) => ({ kind: m.kind, label: m.label, cost: m.cost })),
        })),
    );
    return { lines, skipped };
}

export const templateTotal = (template: Pick<InvoiceTemplate, "lines">) => draftTotals(templateRows(template)).total;

/** "1, 5" — or "Tag 1: 1, 5 · Tag 2: 3" for several days (`dayLabel` gives the "Tag n" text). */
export const templateCodesText = (template: Pick<InvoiceTemplate, "lines">, dayLabel: (n: number) => string) => {
    const days = templateDayCount(template);
    const codes = (d?: number) => templateDayLines(template, d).map((l) => l.code).join(", ");
    return days <= 1 ? codes() : Array.from({ length: days }, (_, d) => `${dayLabel(d + 1)}: ${codes(d)}`).join(" · ");
};

/** An untouched empty line (step 3's starter row) — replaced by an inserted template instead of kept. */
const isUntouched = (r: GoaLineDraft) => !r.code.trim() && !r.description.trim() && !(r.price > 0) && r.materialLines.length === 0;

/** Day rows after inserting one template day (default: all lines): appended; an untouched empty line goes away. */
export const insertTemplate = (rows: GoaLineDraft[], template: Pick<InvoiceTemplate, "lines">, day?: number): GoaLineDraft[] => [
    ...rows.filter((r) => !isUntouched(r)),
    ...templateRows(template, day),
];

/**
 * Inserts a (multi-day) template starting at treatment day `from`: template day 1 → `from`, day 2 → the next
 * treatment day, … Template days beyond the last treatment day aren't inserted — `skippedDays` counts them.
 */
export function insertTemplateDays(
    services: Record<string, GoaLineDraft[]>,
    treatmentDays: string[],
    from: string,
    template: Pick<InvoiceTemplate, "lines">,
): { services: Record<string, GoaLineDraft[]>; insertedDays: number; skippedDays: number } {
    const start = treatmentDays.indexOf(from);
    const count = templateDayCount(template);
    const next = { ...services };
    let insertedDays = 0;
    for (let d = 0; d < count; d++) {
        const target = start >= 0 ? treatmentDays[start + d] : undefined;
        if (!target) break;
        next[target] = insertTemplate(next[target] ?? [], template, d);
        insertedDays++;
    }
    return { services: next, insertedDays, skippedDays: count - insertedDays };
}
